import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { getOccasions, type OccasionIndexQueryKeyEntries } from './occasions.api'
import type { OccasionKind } from './occasions.types'

export const OCCASION_KIND_DATE_BACKED = 'date_backed'
export const OCCASION_KIND_EVERGREEN = 'evergreen'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface OccasionIndexScope extends OrganizationScope {
  kind?: OccasionKind | undefined
}

/** The key entries `getOccasions` reads; `occasionQueryKeys.index` sets them. */
type OccasionIndexQueryKey = [AdminQueryRoot & OccasionIndexQueryKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const occasionQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'occasion' }, { organizationId }),
  ],
  index: ({ organizationId, kind }: OccasionIndexScope = {}): OccasionIndexQueryKey =>
    buildScopedAdminQueryKey(occasionQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      kind,
    }),
}

export const occasionQueries = {
  index: ({ organizationId, kind }: OccasionIndexScope = {}) =>
    queryOptions({
      queryKey: occasionQueryKeys.index({ organizationId, kind }),
      queryFn: getOccasions,
      staleTime: 1000 * 60 * 15,
    }),
}

type OccasionsOptions = OccasionIndexScope & {
  params?: QueryParams<ReturnType<typeof occasionQueries.index>>
}

export function useOccasions({ organizationId, kind, params = {} }: OccasionsOptions = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...occasionQueries.index({ organizationId: resolvedOrganizationId, kind }),
    placeholderData: [],
    ...params,
  })
}

export function useDateBackedOccasions(options: Omit<OccasionsOptions, 'kind'> = {}) {
  return useOccasions({ ...options, kind: OCCASION_KIND_DATE_BACKED })
}
